// Purchases.jsx — analysis of supplier invoices uploaded from Retm's purchases report.
// "purch" data shape:
//   { ref, supplier, tax_id, invoice_num, date, raw_date, created_at,
//     pre_tax, tax, total, draft }
// `draft = true` means the invoice is "لم تنتهي" — we still display it as a row but
// exclude it from every aggregation so totals don't get poisoned with zeros.

import React, { useMemo, useEffect } from "react";
import { Kpi } from "./Dashboard";
import { mkC } from "./lib/chartFactory";
import { downloadCSV } from "./lib/export";

const fm = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");
const fm2 = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

export default function Purchases({ purch, onUpload, canUpload }) {
  const hasData = Array.isArray(purch) && purch.length > 0;

  const closed = useMemo(() => (purch || []).filter((p) => !p.draft && p.total > 0), [purch]);
  const draftCount = (purch || []).length - closed.length;

  const stats = useMemo(() => {
    const totalGross = closed.reduce((s, p) => s + p.total, 0);
    const totalNet   = closed.reduce((s, p) => s + p.pre_tax, 0);
    const totalVat   = closed.reduce((s, p) => s + p.tax, 0);

    // Suppliers
    const bySupplier = new Map();
    for (const p of closed) {
      let s = bySupplier.get(p.supplier);
      if (!s) { s = { name: p.supplier, count: 0, total: 0, net: 0, vat: 0, lastDate: "" }; bySupplier.set(p.supplier, s); }
      s.count++;
      s.total += p.total;
      s.net += p.pre_tax;
      s.vat += p.tax;
      if ((p.date || "") > s.lastDate) s.lastDate = p.date || "";
    }
    const suppliers = Array.from(bySupplier.values()).sort((a, b) => b.total - a.total);

    // Daily aggregation (sorted)
    const byDay = new Map();
    for (const p of closed) {
      const k = p.date || p.raw_date || "—";
      const v = byDay.get(k) || { date: k, total: 0, count: 0 };
      v.total += p.total;
      v.count++;
      byDay.set(k, v);
    }
    const days = Array.from(byDay.values()).sort((a, b) => a.date.localeCompare(b.date));

    // Monthly aggregation
    const byMonth = new Map();
    for (const p of closed) {
      const ym = (p.date || "").slice(0, 7) || "—";
      const v = byMonth.get(ym) || { month: ym, total: 0, count: 0 };
      v.total += p.total;
      v.count++;
      byMonth.set(ym, v);
    }
    const months = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));

    const dateMin = days.length ? days[0].date : "";
    const dateMax = days.length ? days[days.length - 1].date : "";
    const span = days.length;
    const avgPerInvoice = closed.length ? totalGross / closed.length : 0;
    const avgPerDay = span ? totalGross / span : 0;

    // Last 30 days vs previous 30 days (rough trend)
    const recent = days.slice(-30).reduce((s, d) => s + d.total, 0);
    const prev   = days.slice(-60, -30).reduce((s, d) => s + d.total, 0);
    const trendPct = prev > 0 ? ((recent - prev) / prev * 100) : null;

    return { totalGross, totalNet, totalVat, suppliers, days, months, dateMin, dateMax, span, avgPerInvoice, avgPerDay, recent, prev, trendPct };
  }, [closed]);

  // Build charts
  useEffect(() => {
    if (!closed.length) return;
    // Top 10 suppliers
    const top = stats.suppliers.slice(0, 10);
    mkC("c-purch-suppliers", "bar", top.map((s) => s.name), [{
      label: "إجمالي الإنفاق (شامل الضريبة)",
      data: top.map((s) => +s.total.toFixed(2)),
      backgroundColor: "rgba(184,134,11,.78)",
      borderRadius: 4,
    }], { indexAxis: "y", scales: { x: { ticks: { callback: (v) => fm(v) + " ر" } } } });

    // Monthly trend
    if (stats.months.length >= 1) {
      mkC("c-purch-monthly", "bar", stats.months.map((m) => m.month), [{
        label: "إنفاق شهري (شامل الضريبة)",
        data: stats.months.map((m) => +m.total.toFixed(2)),
        backgroundColor: "rgba(30,99,144,.72)",
        borderRadius: 4,
      }], { scales: { y: { ticks: { callback: (v) => fm(v) + " ر" } } } });
    }

    // Daily trend (last 60 days for readability)
    if (stats.days.length >= 2) {
      const slice = stats.days.slice(-60);
      mkC("c-purch-daily", "line", slice.map((d) => d.date.slice(5)), [{
        label: "إنفاق يومي",
        data: slice.map((d) => +d.total.toFixed(2)),
        borderColor: "#B8860B",
        backgroundColor: "rgba(184,134,11,.15)",
        tension: 0.3,
        fill: true,
        pointRadius: 2,
      }], { scales: { y: { ticks: { callback: (v) => fm(v) + " ر" } } } });
    }
  });

  // CSV export
  const exportCsv = () => {
    const headers = ["#", "المرجع", "المورد", "رقم الفاتورة", "تاريخ الفاتورة", "قبل الضريبة", "الضريبة", "الإجمالي", "الحالة"];
    const rows = purch.map((p, i) => [
      i + 1,
      p.ref || "",
      p.supplier || "",
      p.invoice_num || "",
      p.date || p.raw_date || "",
      p.draft ? "—" : p.pre_tax.toFixed(2),
      p.draft ? "—" : p.tax.toFixed(2),
      p.draft ? "—" : p.total.toFixed(2),
      p.draft ? "مسوّدة" : "مكتملة",
    ]);
    const today = new Date().toISOString().slice(0, 10);
    downloadCSV(`المشتريات_${today}.csv`, headers, rows);
  };

  // Empty state — invite the user to upload. Placed AFTER hooks to keep hook order stable.
  if (!hasData) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 30 }} data-testid="purch-empty">
        <div style={{ fontSize: 40, marginBottom: 8 }}>🛒</div>
        <h3 style={{ margin: 0, color: "var(--brown-deep)" }}>تقرير المشتريات</h3>
        <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.9, marginTop: 8 }}>
          ارفع تقرير المشتريات من رتم (Purchases Report) لرؤية تحليلات الموردين، الإنفاق الشهري، الفواتير المسوّدة، والاتجاهات.
        </p>
        {canUpload && (
          <button
            type="button"
            className="btn btn-success"
            onClick={onUpload}
            data-testid="purch-upload-btn"
            style={{ marginTop: 14 }}
          >📥 رفع ملف المشتريات</button>
        )}
        <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 16, lineHeight: 1.8 }}>
          <b>الأعمدة المتوقّعة في الملف:</b><br />
          المرجع · اسم المورد · رقم الفاتورة · تاريخ الفاتورة · القيمة قبل الضريبة · الضريبة · القيمة شامل الضريبة
        </div>
      </div>
    );
  }

  return (
    <div data-testid="purchases-tab">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: "var(--brown-deep)" }}>🛒 المشتريات</h3>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          {stats.dateMin && stats.dateMax ? `${stats.dateMin} → ${stats.dateMax}` : "—"}
        </span>
        <div style={{ marginInlineStart: "auto", display: "flex", gap: 8 }}>
          {canUpload && (
            <button type="button" className="btn btn-soft" onClick={onUpload} data-testid="purch-reupload">📥 رفع ملف جديد</button>
          )}
          <button type="button" className="btn btn-soft" onClick={exportCsv} data-testid="purch-export">⬇ تصدير CSV</button>
        </div>
      </div>

      <Kpi items={[
        { l: "عدد الفواتير", v: closed.length.toLocaleString("en-US"), s: draftCount > 0 ? `${draftCount} مسوّدة لم تنتهِ` : "كلها مكتملة" },
        { l: "إجمالي الإنفاق", v: fm(stats.totalGross) + " ر", s: "شامل الضريبة" },
        { l: "صافي بدون ضريبة", v: fm(stats.totalNet) + " ر", c: "b" },
        { l: "إجمالي الضريبة", v: fm(stats.totalVat) + " ر", c: "o" },
        { l: "متوسط الفاتورة", v: fm2(stats.avgPerInvoice) + " ر", s: "للفاتورة الواحدة" },
        { l: "متوسط الإنفاق اليومي", v: fm2(stats.avgPerDay) + " ر", s: `على ${stats.span} يوم` },
        { l: "عدد الموردين", v: stats.suppliers.length.toLocaleString("en-US") },
        {
          l: "اتجاه 30 يوم",
          v: stats.trendPct === null ? "—" : (stats.trendPct >= 0 ? "▲" : "▼") + " " + Math.abs(stats.trendPct).toFixed(1) + "%",
          s: stats.trendPct === null ? "بيانات قليلة" : (stats.trendPct >= 0 ? "ارتفع عن الـ30 يوم السابقة" : "انخفض"),
          c: stats.trendPct === null ? "" : (stats.trendPct < 0 ? "g" : "r"),
        },
      ]} />

      <div className="g2" style={{ marginTop: 14 }}>
        <div className="card">
          <h4>🏆 أعلى 10 موردين</h4>
          <div className="cw" style={{ height: 320 }}><canvas id="c-purch-suppliers"></canvas></div>
        </div>
        <div className="card">
          <h4>📅 الإنفاق الشهري</h4>
          <div className="cw" style={{ height: 320 }}><canvas id="c-purch-monthly"></canvas></div>
        </div>
      </div>

      {stats.days.length >= 2 && (
        <div className="card" style={{ marginTop: 14 }}>
          <h4>📈 الاتجاه اليومي (آخر 60 يوم)</h4>
          <div className="cw" style={{ height: 260 }}><canvas id="c-purch-daily"></canvas></div>
        </div>
      )}

      {/* Suppliers table */}
      <div className="card" style={{ marginTop: 14 }} data-testid="suppliers-table">
        <h4>📋 تفاصيل الموردين</h4>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>المورد</th>
                <th>عدد الفواتير</th>
                <th>إجمالي</th>
                <th>صافي</th>
                <th>ضريبة</th>
                <th>متوسط/فاتورة</th>
                <th>آخر فاتورة</th>
              </tr>
            </thead>
            <tbody>
              {stats.suppliers.map((s, i) => (
                <tr key={s.name}>
                  <td>{i + 1}</td>
                  <td><b>{s.name}</b></td>
                  <td>{s.count}</td>
                  <td><b>{fm2(s.total)}</b> ر</td>
                  <td>{fm2(s.net)} ر</td>
                  <td>{fm2(s.vat)} ر</td>
                  <td>{fm2(s.total / s.count)} ر</td>
                  <td>{s.lastDate || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All invoices table */}
      <div className="card" style={{ marginTop: 14 }} data-testid="invoices-table">
        <h4>🧾 جميع الفواتير ({purch.length.toLocaleString("en-US")})</h4>
        <div className="tw" style={{ maxHeight: 500, overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>المرجع</th>
                <th>المورد</th>
                <th>التاريخ</th>
                <th>قبل الضريبة</th>
                <th>الضريبة</th>
                <th>الإجمالي</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {[...purch].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((p, i) => (
                <tr key={`${p.ref || p.invoice_num}-${i}`} style={p.draft ? { opacity: 0.55 } : null}>
                  <td>{i + 1}</td>
                  <td style={{ fontSize: 11 }}>{p.ref || "—"}</td>
                  <td>{p.supplier}</td>
                  <td style={{ fontSize: 11 }}>{p.date || p.raw_date || "—"}</td>
                  <td>{p.draft ? "—" : fm2(p.pre_tax)}</td>
                  <td>{p.draft ? "—" : fm2(p.tax)}</td>
                  <td><b>{p.draft ? "—" : fm2(p.total)}</b></td>
                  <td>{p.draft
                    ? <span className="b" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>مسوّدة</span>
                    : <span className="b bg">مكتملة</span>
                  }</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
